// ─────────────────────────────────────────────────────────────────────────────
// Industry question knowledge base
//
// Curated, hand-crafted data that powers the "AI Generate" guided form builder.
// Each industry exposes a top-level "what type of project" question whose options
// are the services a business offers. Selecting a service can reveal a single
// follow-up drill-down question (a conditional branch). Options flagged
// `lowIntent` become request-only pre-qualifiers when the user's goal is to
// collect *qualified* leads (they route to a request instead of instant booking).
//
// This file is intentionally pure data so it is trivial to extend (add an
// industry / service / follow-up) and so a backend LLM can later produce the
// same shape behind the wizard's `onGenerate` contract.
// ─────────────────────────────────────────────────────────────────────────────

export type TemplateQuestionType = 'radio' | 'select' | 'checkbox' | 'text' | 'textarea' | 'file';

export interface TemplateOption {
  label: string;
  /**
   * Low-intent answer. When the goal is "qualified leads" this option is marked
   * request-only so the lead is reviewed instead of instantly booked.
   */
  lowIntent?: boolean;
  /**
   * Label for an optional photo upload asked only of customers who pick this
   * answer. Set on repair branches, where a picture of the actual problem is
   * what makes the job quotable before anyone drives out.
   */
  photoPrompt?: string;
}

export interface TemplateQuestion {
  /** Stable id within the template (used to wire follow-ups / toggles). */
  id: string;
  label: string;
  type: TemplateQuestionType;
  options?: TemplateOption[];
  required?: boolean;
}

export interface ServiceOption {
  id: string;
  label: string;
  /** Marks this service itself as a low-intent option on the project question. */
  lowIntent?: boolean;
  /** Drill-down question revealed when this service is selected (radio branch). */
  followUp?: TemplateQuestion;
  /**
   * This service isn't described by the project question's specific wording
   * (e.g. garage storage under "what area do you want coated?"). Selecting it
   * swaps the question over to `projectQuestion.genericLabel`.
   */
  outsideLabelScope?: boolean;
}

export interface IndustryTemplate {
  id: string;
  name: string;
  /** Lucide icon name, mapped to a component in the wizard UI. */
  icon: string;
  tagline: string;
  /**
   * Budget ranges tuned to this industry's typical job sizes. Used for the
   * "budget" add-on so we never offer, e.g., $10k+ tiers for lawn maintenance.
   */
  budgetRanges: TemplateOption[];
  /** The top-level "what type of project is it?" question. */
  projectQuestion: {
    label: string;
    /**
     * Broader wording used instead of `label` once a selected service falls
     * outside it (see `ServiceOption.outsideLabelScope`).
     */
    genericLabel?: string;
    services: ServiceOption[];
  };
  /**
   * Extra add-on questions offered only for this industry, alongside the common
   * ones. Used for trade-specific upsells (e.g. garage storage on top of a
   * concrete coating job).
   */
  addOns?: AddOnQuestion[];
}

export interface AddOnQuestion extends TemplateQuestion {
  description: string;
  /** Whether this add-on is pre-selected in the review step. */
  defaultEnabled: boolean;
  /** Pre-select this add-on only when the goal is "qualified leads". */
  qualifiedOnly?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Common, cross-industry add-on questions (always optional, always visible)
// ─────────────────────────────────────────────────────────────────────────────

export const COMMON_ADD_ONS: AddOnQuestion[] = [
  {
    id: 'timeline',
    label: 'When are you hoping to get started?',
    description: 'Gauges urgency and filters out tire-kickers.',
    type: 'radio',
    defaultEnabled: true,
    options: [
      { label: 'As soon as possible' },
      { label: 'Within the next month' },
      { label: 'In 1-3 months' },
      { label: "Just researching for now", lowIntent: true },
    ],
  },
  {
    id: 'property_type',
    label: 'Is this for a home or a business?',
    description: 'Helps you route and price residential vs commercial jobs.',
    type: 'radio',
    defaultEnabled: true,
    options: [
      { label: 'Residential (home)' },
      { label: 'Commercial (business)' },
    ],
  },
  {
    // Options are resolved per-industry at generation time via getBudgetOptions.
    id: 'budget',
    label: "What's your approximate budget?",
    description: 'Qualifies serious buyers. Great for filtering bookings.',
    type: 'radio',
    defaultEnabled: false,
  },
  {
    id: 'photos',
    label: 'Add photos of the project',
    description: 'Lets you scope and quote accurately before the visit.',
    type: 'file',
    defaultEnabled: false,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Industries
// ─────────────────────────────────────────────────────────────────────────────

export const INDUSTRY_TEMPLATES: IndustryTemplate[] = [
  {
    id: 'painting',
    name: 'Painting',
    icon: 'PaintBucket',
    tagline: 'Interior, exterior, cabinets & commercial',
    budgetRanges: [
      { label: 'Under $1,000' },
      { label: '$1,000 - $3,000' },
      { label: '$3,000 - $7,000' },
      { label: '$7,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What type of painting project is it?',
      services: [
        {
          id: 'interior',
          label: 'Interior painting',
          followUp: {
            id: 'interior_scope',
            label: 'What needs painting inside?',
            type: 'radio',
            options: [
              { label: 'Whole or most of the house' },
              { label: 'A few rooms', lowIntent: true },
              { label: 'Just one room or a small area', lowIntent: true },
            ],
          },
        },
        {
          id: 'exterior',
          label: 'Exterior painting',
          followUp: {
            id: 'exterior_scope',
            label: "What's the exterior scope?",
            type: 'radio',
            options: [
              { label: 'Whole house exterior' },
              { label: 'Trim & accents only', lowIntent: true },
              { label: 'Deck, fence, or shed', lowIntent: true },
            ],
          },
        },
        {
          id: 'cabinets',
          label: 'Cabinet refinishing',
          followUp: {
            id: 'cabinet_scope',
            label: 'Which cabinets need refinishing?',
            type: 'radio',
            options: [
              { label: 'Entire kitchen' },
              { label: 'Kitchen + bathrooms' },
              { label: 'Touch-ups / small area', lowIntent: true },
            ],
          },
        },
        {
          id: 'commercial',
          label: 'Commercial painting',
          followUp: {
            id: 'commercial_space',
            label: "What's the commercial space?",
            type: 'radio',
            options: [
              { label: 'Office' },
              { label: 'Retail storefront' },
              { label: 'Warehouse or industrial' },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'roofing',
    name: 'Roofing',
    icon: 'Home',
    tagline: 'Repairs, replacements & inspections',
    budgetRanges: [
      { label: 'Under $5,000' },
      { label: '$5,000 - $12,000' },
      { label: '$12,000 - $25,000' },
      { label: '$25,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What do you need help with?',
      services: [
        {
          id: 'repair',
          label: 'Roof repair',
          followUp: {
            id: 'repair_issue',
            label: "What's the issue?",
            type: 'radio',
            options: [
              { label: 'Active leak', photoPrompt: 'Add a photo of the leak or water stain' },
              { label: 'Storm or wind damage', photoPrompt: 'Add a photo of the storm damage' },
              {
                label: 'Missing or damaged shingles',
                photoPrompt: 'Add a photo of the damaged shingles',
              },
              {
                label: 'General wear and tear',
                lowIntent: true,
                photoPrompt: 'Add a photo of the roof',
              },
            ],
          },
        },
        {
          id: 'replacement',
          label: 'Roof replacement',
          followUp: {
            id: 'replacement_material',
            label: "What's the roof material?",
            type: 'radio',
            options: [
              { label: 'Asphalt shingles' },
              { label: 'Metal' },
              { label: 'Tile' },
              { label: 'Flat / rubber' },
              { label: "I'm not sure", lowIntent: true },
            ],
          },
        },
        {
          id: 'inspection',
          label: 'Inspection / estimate',
          lowIntent: true,
          followUp: {
            id: 'inspection_reason',
            label: "What's prompting the inspection?",
            type: 'radio',
            options: [
              { label: 'Buying or selling a home' },
              { label: 'Insurance claim' },
              { label: 'Just want it checked out', lowIntent: true },
            ],
          },
        },
        {
          id: 'newconstruction',
          label: 'New construction',
          followUp: {
            id: 'newconstruction_stage',
            label: 'What stage is the project?',
            type: 'radio',
            options: [
              { label: 'Ready to build' },
              { label: 'Still planning', lowIntent: true },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'remodeling',
    name: 'Remodeling',
    icon: 'Hammer',
    tagline: 'Kitchens, baths, basements & additions',
    budgetRanges: [
      { label: 'Under $10,000' },
      { label: '$10,000 - $30,000' },
      { label: '$30,000 - $75,000' },
      { label: '$75,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What are you looking to remodel?',
      services: [
        {
          id: 'kitchen',
          label: 'Kitchen',
          followUp: {
            id: 'kitchen_scope',
            label: "What's the kitchen scope?",
            type: 'radio',
            options: [
              { label: 'Full remodel' },
              { label: 'Cabinets & countertops' },
              { label: 'Minor updates', lowIntent: true },
            ],
          },
        },
        {
          id: 'bathroom',
          label: 'Bathroom',
          followUp: {
            id: 'bathroom_scope',
            label: "What's the bathroom scope?",
            type: 'radio',
            options: [
              { label: 'Full remodel' },
              { label: 'Tub-to-shower conversion' },
              { label: 'Minor updates', lowIntent: true },
            ],
          },
        },
        {
          id: 'basement',
          label: 'Basement',
          followUp: {
            id: 'basement_scope',
            label: "What's the basement project?",
            type: 'radio',
            options: [
              { label: 'Finish an unfinished basement' },
              { label: 'Remodel an existing basement' },
              { label: 'Just exploring options', lowIntent: true },
            ],
          },
        },
        {
          id: 'addition',
          label: 'Home addition',
          followUp: {
            id: 'addition_type',
            label: 'What kind of addition?',
            type: 'radio',
            options: [
              { label: 'Room addition' },
              { label: 'Second story' },
              { label: 'Garage' },
              { label: 'Not sure yet', lowIntent: true },
            ],
          },
        },
        {
          id: 'wholehome',
          label: 'Whole-home renovation',
          followUp: {
            id: 'wholehome_timeline',
            label: 'How soon are you looking to start?',
            type: 'radio',
            options: [
              { label: 'Ready now' },
              { label: 'Planning for later', lowIntent: true },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'landscaping',
    name: 'Landscaping & Lawn',
    icon: 'Trees',
    tagline: 'Lawn care, design, hardscaping & trees',
    budgetRanges: [
      { label: 'Under $500' },
      { label: '$500 - $2,500' },
      { label: '$2,500 - $10,000' },
      { label: '$10,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What do you need?',
      services: [
        {
          id: 'maintenance',
          label: 'Lawn maintenance',
          followUp: {
            id: 'maintenance_frequency',
            label: 'How often do you need service?',
            type: 'radio',
            options: [
              { label: 'Weekly' },
              { label: 'Bi-weekly' },
              { label: 'One-time cleanup', lowIntent: true },
            ],
          },
        },
        {
          id: 'design',
          label: 'Landscape design',
          followUp: {
            id: 'design_scope',
            label: "What's the scope?",
            type: 'radio',
            options: [
              { label: 'Full yard redesign' },
              { label: 'A specific area' },
              { label: 'Just looking for ideas', lowIntent: true },
            ],
          },
        },
        {
          id: 'hardscaping',
          label: 'Hardscaping / patios',
          followUp: {
            id: 'hardscaping_type',
            label: 'What are you adding?',
            type: 'radio',
            options: [
              { label: 'Patio' },
              { label: 'Walkway' },
              { label: 'Retaining wall' },
              { label: 'Outdoor kitchen' },
            ],
          },
        },
        {
          id: 'tree',
          label: 'Tree service',
          followUp: {
            id: 'tree_need',
            label: "What's needed?",
            type: 'radio',
            options: [
              { label: 'Tree removal' },
              { label: 'Trimming / pruning' },
              { label: 'Stump grinding' },
              { label: 'Storm cleanup' },
            ],
          },
        },
        {
          id: 'irrigation',
          label: 'Irrigation / sprinklers',
          followUp: {
            id: 'irrigation_need',
            label: "What's the need?",
            type: 'radio',
            options: [
              { label: 'New installation' },
              { label: 'Repair' },
              { label: 'Seasonal start-up / winterizing', lowIntent: true },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'hvac',
    name: 'HVAC',
    icon: 'Wind',
    tagline: 'Heating, cooling, install & maintenance',
    budgetRanges: [
      { label: 'Under $500' },
      { label: '$500 - $3,000' },
      { label: '$3,000 - $8,000' },
      { label: '$8,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What can we help with?',
      services: [
        {
          id: 'repair',
          label: 'Repair',
          followUp: {
            id: 'repair_symptom',
            label: "What's going on?",
            type: 'radio',
            options: [
              { label: 'Not cooling', photoPrompt: 'Add a photo of your AC unit and thermostat' },
              { label: 'Not heating', photoPrompt: 'Add a photo of your furnace or heater' },
              {
                label: 'Strange noises or smells',
                photoPrompt: 'Add a photo of the unit making the noise',
              },
              {
                label: 'No power / not turning on',
                photoPrompt: 'Add a photo of the unit and its breaker',
              },
            ],
          },
        },
        {
          id: 'install',
          label: 'Install / replace',
          followUp: {
            id: 'install_system',
            label: 'What system are you interested in?',
            type: 'radio',
            options: [
              { label: 'Central air conditioning' },
              { label: 'Furnace' },
              { label: 'Heat pump' },
              { label: 'Mini-split / ductless' },
              { label: "I'm not sure", lowIntent: true },
            ],
          },
        },
        {
          id: 'maintenance',
          label: 'Maintenance / tune-up',
          lowIntent: true,
          followUp: {
            id: 'maintenance_system',
            label: 'Which system needs service?',
            type: 'radio',
            options: [
              { label: 'AC' },
              { label: 'Furnace' },
              { label: 'Both' },
            ],
          },
        },
        {
          id: 'airquality',
          label: 'Indoor air quality',
          followUp: {
            id: 'airquality_concern',
            label: "What's the concern?",
            type: 'radio',
            options: [
              { label: 'Air purification' },
              { label: 'Humidifier / dehumidifier' },
              { label: 'Duct cleaning' },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'plumbing',
    name: 'Plumbing',
    icon: 'Droplets',
    tagline: 'Repairs, drains, water heaters & remodels',
    budgetRanges: [
      { label: 'Under $500' },
      { label: '$500 - $1,500' },
      { label: '$1,500 - $5,000' },
      { label: '$5,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: "What's the plumbing job?",
      services: [
        {
          id: 'repair',
          label: 'Repair',
          followUp: {
            id: 'repair_issue',
            label: "What's the issue?",
            type: 'radio',
            options: [
              { label: 'Leak', photoPrompt: 'Add a photo of the leak' },
              { label: 'Clog or backup', photoPrompt: 'Add a photo of the drain or fixture' },
              { label: 'No hot water', photoPrompt: 'Add a photo of your water heater' },
              { label: 'Running or broken toilet', photoPrompt: 'Add a photo of the toilet' },
              { label: 'Something else', photoPrompt: 'Add a photo of the problem' },
            ],
          },
        },
        {
          id: 'drain',
          label: 'Drain cleaning',
          followUp: {
            id: 'drain_location',
            label: "Where's the problem?",
            type: 'radio',
            options: [
              { label: 'Kitchen' },
              { label: 'Bathroom' },
              { label: 'Main line' },
              { label: 'Multiple drains' },
            ],
          },
        },
        {
          id: 'waterheater',
          label: 'Water heater',
          followUp: {
            id: 'waterheater_need',
            label: 'What do you need?',
            type: 'radio',
            options: [
              { label: 'Repair' },
              { label: 'Replacement' },
              { label: 'New tankless install' },
            ],
          },
        },
        {
          id: 'remodel',
          label: 'Remodel / new fixtures',
          followUp: {
            id: 'remodel_scope',
            label: "What's the scope?",
            type: 'radio',
            options: [
              { label: 'A single fixture', lowIntent: true },
              { label: 'Whole bathroom' },
              { label: 'Whole kitchen' },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'electrical',
    name: 'Electrical',
    icon: 'Zap',
    tagline: 'Repairs, panels, lighting & EV chargers',
    budgetRanges: [
      { label: 'Under $500' },
      { label: '$500 - $2,000' },
      { label: '$2,000 - $6,000' },
      { label: '$6,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What electrical work do you need?',
      services: [
        {
          id: 'repair',
          label: 'Repair',
          followUp: {
            id: 'repair_problem',
            label: "What's the problem?",
            type: 'radio',
            options: [
              {
                label: 'Outlet or switch not working',
                photoPrompt: 'Add a photo of the outlet or switch',
              },
              { label: 'Flickering lights', photoPrompt: 'Add a photo of the light or fixture' },
              { label: 'Breaker keeps tripping', photoPrompt: 'Add a photo of your breaker panel' },
              { label: 'Something else', photoPrompt: 'Add a photo of the problem' },
            ],
          },
        },
        {
          id: 'panel',
          label: 'Panel upgrade',
          followUp: {
            id: 'panel_situation',
            label: "What's the situation?",
            type: 'radio',
            options: [
              { label: 'Upgrading capacity' },
              { label: 'Replacing an old panel' },
              { label: 'Adding circuits' },
            ],
          },
        },
        {
          id: 'lighting',
          label: 'Lighting install',
          followUp: {
            id: 'lighting_type',
            label: 'What kind of lighting?',
            type: 'radio',
            options: [
              { label: 'Indoor fixtures' },
              { label: 'Recessed lighting' },
              { label: 'Outdoor / landscape' },
              { label: 'Ceiling fans' },
            ],
          },
        },
        {
          id: 'ev',
          label: 'EV charger install',
          followUp: {
            id: 'ev_setup',
            label: "What's your setup?",
            type: 'radio',
            options: [
              { label: 'I already have the charger' },
              { label: 'I need a recommendation', lowIntent: true },
            ],
          },
        },
        {
          id: 'generator',
          label: 'Generator',
          followUp: {
            id: 'generator_type',
            label: 'What type of generator?',
            type: 'radio',
            options: [
              { label: 'Whole-home standby' },
              { label: 'Portable hookup' },
              { label: 'Just researching', lowIntent: true },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'cleaning',
    name: 'Cleaning',
    icon: 'Sparkles',
    tagline: 'Home & commercial cleaning services',
    budgetRanges: [
      { label: 'Under $150' },
      { label: '$150 - $400' },
      { label: '$400 - $1,000' },
      { label: '$1,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What type of cleaning do you need?',
      services: [
        {
          id: 'standard',
          label: 'Standard cleaning',
          followUp: {
            id: 'standard_frequency',
            label: 'How often?',
            type: 'radio',
            options: [
              { label: 'Weekly' },
              { label: 'Bi-weekly' },
              { label: 'Monthly' },
              { label: 'One-time', lowIntent: true },
            ],
          },
        },
        {
          id: 'deep',
          label: 'Deep cleaning',
          followUp: {
            id: 'deep_focus',
            label: "What's the focus?",
            type: 'radio',
            options: [
              { label: 'Whole home' },
              { label: 'Kitchen & bathrooms' },
              { label: 'Specific rooms', lowIntent: true },
            ],
          },
        },
        {
          id: 'moveinout',
          label: 'Move in / out cleaning',
          followUp: {
            id: 'moveinout_which',
            label: 'Which is it?',
            type: 'radio',
            options: [
              { label: 'Move-out' },
              { label: 'Move-in' },
              { label: 'Both' },
            ],
          },
        },
        {
          id: 'commercial',
          label: 'Commercial / office',
          followUp: {
            id: 'commercial_space',
            label: "What's the space?",
            type: 'radio',
            options: [
              { label: 'Office' },
              { label: 'Retail' },
              { label: 'Medical' },
              { label: 'Other' },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'pestcontrol',
    name: 'Pest Control',
    icon: 'Bug',
    tagline: 'General pests, termites, rodents & wildlife',
    budgetRanges: [
      { label: 'Under $200' },
      { label: '$200 - $500' },
      { label: '$500 - $1,500' },
      { label: '$1,500+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: "What's the pest issue?",
      services: [
        {
          id: 'general',
          label: 'General pest control',
          followUp: {
            id: 'general_pest',
            label: 'What are you seeing?',
            type: 'radio',
            options: [
              { label: 'Ants' },
              { label: 'Spiders' },
              { label: 'Roaches' },
              { label: 'General prevention', lowIntent: true },
            ],
          },
        },
        {
          id: 'termite',
          label: 'Termites',
          followUp: {
            id: 'termite_need',
            label: 'What do you need?',
            type: 'radio',
            options: [
              { label: 'Active infestation' },
              { label: 'Inspection', lowIntent: true },
              { label: 'Prevention' },
            ],
          },
        },
        {
          id: 'rodent',
          label: 'Rodent control',
          followUp: {
            id: 'rodent_situation',
            label: "What's the situation?",
            type: 'radio',
            options: [
              { label: 'Mice' },
              { label: 'Rats' },
              { label: "I'm not sure", lowIntent: true },
            ],
          },
        },
        {
          id: 'mosquito',
          label: 'Mosquito / outdoor',
          followUp: {
            id: 'mosquito_goal',
            label: "What's the goal?",
            type: 'radio',
            options: [
              { label: 'One-time event treatment', lowIntent: true },
              { label: 'Seasonal treatment plan' },
            ],
          },
        },
        {
          id: 'wildlife',
          label: 'Wildlife removal',
          followUp: {
            id: 'wildlife_animal',
            label: 'What animal?',
            type: 'radio',
            options: [
              { label: 'Raccoons' },
              { label: 'Squirrels' },
              { label: 'Birds' },
              { label: 'Other' },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'flooring',
    name: 'Flooring',
    icon: 'Layers',
    tagline: 'Hardwood, tile, carpet, laminate & vinyl',
    budgetRanges: [
      { label: 'Under $2,000' },
      { label: '$2,000 - $6,000' },
      { label: '$6,000 - $15,000' },
      { label: '$15,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What flooring are you interested in?',
      services: [
        {
          id: 'hardwood',
          label: 'Hardwood',
          followUp: {
            id: 'hardwood_job',
            label: "What's the job?",
            type: 'radio',
            options: [
              { label: 'New installation' },
              { label: 'Refinish existing' },
              { label: 'Repair', lowIntent: true },
            ],
          },
        },
        {
          id: 'tile',
          label: 'Tile',
          followUp: {
            id: 'tile_location',
            label: "Where's the tile going?",
            type: 'radio',
            options: [
              { label: 'Floors' },
              { label: 'Bathroom / shower' },
              { label: 'Backsplash', lowIntent: true },
            ],
          },
        },
        {
          id: 'carpet',
          label: 'Carpet',
          followUp: {
            id: 'carpet_scope',
            label: "What's the scope?",
            type: 'radio',
            options: [
              { label: 'Whole home' },
              { label: 'A few rooms', lowIntent: true },
              { label: 'A single room', lowIntent: true },
            ],
          },
        },
        {
          id: 'laminate',
          label: 'Laminate / vinyl',
          followUp: {
            id: 'laminate_area',
            label: "What's the area?",
            type: 'radio',
            options: [
              { label: 'Whole home' },
              { label: 'Specific rooms', lowIntent: true },
            ],
          },
        },
        {
          id: 'repair',
          label: 'Repair / restoration',
          lowIntent: true,
          followUp: {
            id: 'repair_fix',
            label: 'What needs fixing?',
            type: 'radio',
            options: [
              { label: 'Water damage', photoPrompt: 'Add a photo of the water damage' },
              { label: 'Scratches or wear', photoPrompt: 'Add a photo of the worn area' },
              { label: 'Replacing sections', photoPrompt: 'Add a photo of the section to replace' },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'garagedoors',
    name: 'Garage Doors',
    icon: 'Warehouse',
    tagline: 'Repairs, openers & new door installs',
    budgetRanges: [
      { label: 'Under $300' },
      { label: '$300 - $1,000' },
      { label: '$1,000 - $3,000' },
      { label: '$3,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What do you need?',
      services: [
        {
          id: 'repair',
          label: 'Repair',
          followUp: {
            id: 'repair_problem',
            label: "What's wrong?",
            type: 'radio',
            options: [
              { label: "Won't open or close", photoPrompt: 'Add a photo of the door and opener' },
              {
                label: 'Broken spring',
                photoPrompt: 'Add a photo of the spring above the door',
              },
              { label: 'Off track', photoPrompt: 'Add a photo of the door off its track' },
              {
                label: 'Noisy operation',
                lowIntent: true,
                photoPrompt: 'Add a photo of the door and opener',
              },
            ],
          },
        },
        {
          id: 'opener',
          label: 'Opener install / repair',
          followUp: {
            id: 'opener_need',
            label: 'What do you need?',
            type: 'radio',
            options: [
              { label: 'New opener' },
              { label: 'Repair existing' },
              { label: 'Smart / Wi-Fi upgrade' },
            ],
          },
        },
        {
          id: 'install',
          label: 'New door install',
          followUp: {
            id: 'install_situation',
            label: "What's the situation?",
            type: 'radio',
            options: [
              { label: 'Replacing an old door' },
              { label: 'New construction' },
              { label: 'Just getting pricing', lowIntent: true },
            ],
          },
        },
        {
          id: 'maintenance',
          label: 'Tune-up / maintenance',
          lowIntent: true,
          followUp: {
            id: 'maintenance_doors',
            label: 'How many doors?',
            type: 'radio',
            options: [
              { label: 'One' },
              { label: 'Two' },
              { label: 'Three or more' },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'pressurewashing',
    name: 'Pressure Washing',
    icon: 'SprayCan',
    tagline: 'Houses, driveways, decks & roofs',
    budgetRanges: [
      { label: 'Under $200' },
      { label: '$200 - $500' },
      { label: '$500 - $1,500' },
      { label: '$1,500+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What needs cleaning?',
      services: [
        {
          id: 'house',
          label: 'House washing',
          followUp: {
            id: 'house_surface',
            label: 'What surface?',
            type: 'radio',
            options: [
              { label: 'Siding' },
              { label: 'Brick' },
              { label: 'Stucco' },
              { label: "I'm not sure", lowIntent: true },
            ],
          },
        },
        {
          id: 'driveway',
          label: 'Driveway / concrete',
          followUp: {
            id: 'driveway_area',
            label: 'What area?',
            type: 'radio',
            options: [
              { label: 'Driveway' },
              { label: 'Sidewalks' },
              { label: 'Patio' },
              { label: 'Multiple areas' },
            ],
          },
        },
        {
          id: 'deck',
          label: 'Deck / fence',
          followUp: {
            id: 'deck_material',
            label: "What's the material?",
            type: 'radio',
            options: [
              { label: 'Wood' },
              { label: 'Composite' },
              { label: 'Vinyl' },
            ],
          },
        },
        {
          id: 'roof',
          label: 'Roof / gutters',
          followUp: {
            id: 'roof_need',
            label: "What's needed?",
            type: 'radio',
            options: [
              { label: 'Roof soft wash' },
              { label: 'Gutter cleaning' },
              { label: 'Both' },
            ],
          },
        },
        {
          id: 'commercial',
          label: 'Commercial',
          followUp: {
            id: 'commercial_property',
            label: "What's the property?",
            type: 'radio',
            options: [
              { label: 'Storefront' },
              { label: 'Parking lot' },
              { label: 'Building exterior' },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'pools',
    name: 'Pools',
    icon: 'Waves',
    tagline: 'Maintenance, repairs & new pool builds',
    budgetRanges: [
      { label: 'Under $500' },
      { label: '$500 - $5,000' },
      { label: '$5,000 - $30,000' },
      { label: '$30,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What do you need help with?',
      services: [
        {
          id: 'maintenance',
          label: 'Pool maintenance / cleaning',
          followUp: {
            id: 'maintenance_frequency',
            label: 'How often do you need service?',
            type: 'radio',
            options: [
              { label: 'Weekly' },
              { label: 'Bi-weekly' },
              { label: 'Opening / closing only' },
              { label: 'One-time cleaning', lowIntent: true },
            ],
          },
        },
        {
          id: 'repair',
          label: 'Pool repair',
          followUp: {
            id: 'repair_issue',
            label: "What's the issue?",
            type: 'radio',
            options: [
              { label: 'Leak', photoPrompt: 'Add a photo of the leak or water level' },
              { label: 'Pump, filter, or heater', photoPrompt: 'Add a photo of the equipment pad' },
              {
                label: 'Liner or surface damage',
                photoPrompt: 'Add a photo of the damaged surface',
              },
              { label: 'Green or cloudy water', photoPrompt: 'Add a photo of the water' },
              { label: 'Something else', photoPrompt: 'Add a photo of the problem' },
            ],
          },
        },
        {
          id: 'build',
          label: 'New pool construction',
          followUp: {
            id: 'build_type',
            label: 'What type of pool?',
            type: 'radio',
            options: [
              { label: 'Inground concrete / gunite' },
              { label: 'Fiberglass' },
              { label: 'Vinyl liner' },
              { label: 'Above-ground' },
              { label: 'Still deciding', lowIntent: true },
            ],
          },
        },
        {
          id: 'remodel',
          label: 'Remodel / resurfacing',
          followUp: {
            id: 'remodel_scope',
            label: "What's the scope?",
            type: 'radio',
            options: [
              { label: 'Resurfacing / replastering' },
              { label: 'Tile or coping' },
              { label: 'Full renovation' },
              { label: 'Just exploring options', lowIntent: true },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'gutters',
    name: 'Gutter Installation',
    icon: 'PanelTop',
    tagline: 'Install, repair, guards & cleaning',
    budgetRanges: [
      { label: 'Under $300' },
      { label: '$300 - $1,000' },
      { label: '$1,000 - $3,000' },
      { label: '$3,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What do you need?',
      services: [
        {
          id: 'install',
          label: 'New gutter installation',
          followUp: {
            id: 'install_type',
            label: 'What type of gutters?',
            type: 'radio',
            options: [
              { label: 'Seamless aluminum' },
              { label: 'Copper' },
              { label: 'Vinyl' },
              { label: "I'm not sure", lowIntent: true },
            ],
          },
        },
        {
          id: 'replacement',
          label: 'Gutter replacement',
          followUp: {
            id: 'replacement_scope',
            label: "What's the scope?",
            type: 'radio',
            options: [
              { label: 'Whole home' },
              { label: 'A section only', lowIntent: true },
            ],
          },
        },
        {
          id: 'repair',
          label: 'Gutter repair',
          followUp: {
            id: 'repair_issue',
            label: "What's the issue?",
            type: 'radio',
            options: [
              { label: 'Leaking or sagging', photoPrompt: 'Add a photo of the sagging section' },
              { label: 'Loose or detached', photoPrompt: 'Add a photo of the loose gutter' },
              { label: 'Damaged downspouts', photoPrompt: 'Add a photo of the downspout' },
              { label: 'Storm damage', photoPrompt: 'Add a photo of the storm damage' },
            ],
          },
        },
        {
          id: 'guards',
          label: 'Gutter guards / leaf protection',
          followUp: {
            id: 'guards_scope',
            label: "What's the scope?",
            type: 'radio',
            options: [
              { label: 'Whole home' },
              { label: 'Problem areas only', lowIntent: true },
            ],
          },
        },
        {
          id: 'cleaning',
          label: 'Gutter cleaning',
          lowIntent: true,
          followUp: {
            id: 'cleaning_frequency',
            label: 'How often?',
            type: 'radio',
            options: [
              { label: 'One-time', lowIntent: true },
              { label: 'Seasonal plan' },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'fencing',
    name: 'Fencing',
    icon: 'Fence',
    tagline: 'Wood, vinyl, chain-link & repairs',
    budgetRanges: [
      { label: 'Under $1,000' },
      { label: '$1,000 - $3,000' },
      { label: '$3,000 - $8,000' },
      { label: '$8,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What do you need?',
      services: [
        {
          id: 'install',
          label: 'New fence installation',
          followUp: {
            id: 'install_material',
            label: 'What material?',
            type: 'radio',
            options: [
              { label: 'Wood' },
              { label: 'Vinyl' },
              { label: 'Chain-link' },
              { label: 'Aluminum / metal' },
              { label: "I'm not sure", lowIntent: true },
            ],
          },
        },
        {
          id: 'repair',
          label: 'Fence repair',
          followUp: {
            id: 'repair_issue',
            label: "What's the issue?",
            type: 'radio',
            options: [
              {
                label: 'Leaning or fallen section',
                photoPrompt: 'Add a photo of the leaning section',
              },
              { label: 'Broken posts', photoPrompt: 'Add a photo of the broken post' },
              { label: 'Storm damage', photoPrompt: 'Add a photo of the storm damage' },
              {
                label: 'General wear',
                lowIntent: true,
                photoPrompt: 'Add a photo of the fence',
              },
            ],
          },
        },
        {
          id: 'gate',
          label: 'Gate install / repair',
          followUp: {
            id: 'gate_need',
            label: 'What do you need?',
            type: 'radio',
            options: [
              { label: 'New gate' },
              { label: 'Repair existing' },
              { label: 'Automatic / electric gate' },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'deckbuilding',
    name: 'Deck Building',
    icon: 'Construction',
    tagline: 'New builds, repairs & staining',
    budgetRanges: [
      { label: 'Under $3,000' },
      { label: '$3,000 - $10,000' },
      { label: '$10,000 - $25,000' },
      { label: '$25,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What type of deck project is it?',
      services: [
        {
          id: 'newbuild',
          label: 'New deck construction',
          followUp: {
            id: 'newbuild_scope',
            label: 'What are you looking to build?',
            type: 'radio',
            options: [
              { label: 'Brand-new deck from scratch' },
              { label: 'Replace an existing deck' },
              { label: 'Add-on or expansion' },
              { label: 'Still planning', lowIntent: true },
            ],
          },
        },
        {
          id: 'repair',
          label: 'Deck repair',
          followUp: {
            id: 'repair_issue',
            label: "What's the issue?",
            type: 'radio',
            options: [
              {
                label: 'Rotting or broken boards',
                photoPrompt: 'Add a photo of the damaged boards',
              },
              { label: 'Loose or wobbly railings', photoPrompt: 'Add a photo of the railing' },
              {
                label: 'Structural / framing issues',
                photoPrompt: 'Add a photo of the framing underneath',
              },
              {
                label: 'General wear',
                lowIntent: true,
                photoPrompt: 'Add a photo of the deck',
              },
            ],
          },
        },
        {
          id: 'staining',
          label: 'Staining / sealing',
          followUp: {
            id: 'staining_scope',
            label: 'What needs staining or sealing?',
            type: 'radio',
            options: [
              { label: 'Whole deck' },
              { label: 'Deck surface only' },
              { label: 'Railings only' },
              { label: 'Not sure yet', lowIntent: true },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'windowsdoors',
    name: 'Windows & Doors',
    icon: 'DoorOpen',
    tagline: 'Window & door install, replace & repair',
    budgetRanges: [
      { label: 'Under $1,000' },
      { label: '$1,000 - $5,000' },
      { label: '$5,000 - $15,000' },
      { label: '$15,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What can we help with?',
      services: [
        {
          id: 'windows',
          label: 'Windows',
          followUp: {
            id: 'windows_job',
            label: "What's the job?",
            type: 'radio',
            options: [
              { label: 'Replace existing windows' },
              { label: 'New installation' },
              { label: 'Single window repair', lowIntent: true },
            ],
          },
        },
        {
          id: 'exteriordoors',
          label: 'Exterior doors',
          followUp: {
            id: 'exteriordoors_type',
            label: 'Which door?',
            type: 'radio',
            options: [
              { label: 'Front / entry door' },
              { label: 'Patio / sliding door' },
              { label: 'Storm door' },
            ],
          },
        },
        {
          id: 'interiordoors',
          label: 'Interior doors',
          followUp: {
            id: 'interiordoors_scope',
            label: "What's the scope?",
            type: 'radio',
            options: [
              { label: 'Multiple doors' },
              { label: 'A single door', lowIntent: true },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'concretecoating',
    name: 'Concrete Coating',
    icon: 'Grid2x2',
    tagline: 'Garage floors, patios, driveways & garage storage',
    budgetRanges: [
      { label: 'Under $1,500' },
      { label: '$1,500 - $3,500' },
      { label: '$3,500 - $7,000' },
      { label: '$7,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What area do you want coated?',
      genericLabel: 'What type of project are you interested in?',
      services: [
        {
          id: 'garage',
          label: 'Garage floor',
          followUp: {
            id: 'garage_size',
            label: 'How big is the garage?',
            type: 'radio',
            options: [
              { label: '1-car (about 250 sq ft)' },
              { label: '2-car (about 400-500 sq ft)' },
              { label: '3-car (about 600-750 sq ft)' },
              { label: '4-car or larger (750+ sq ft)' },
              { label: "I'm not sure", lowIntent: true },
            ],
          },
        },
        {
          id: 'patio',
          label: 'Patio',
          followUp: {
            id: 'patio_size',
            label: "What's the patio size?",
            type: 'radio',
            options: [
              { label: 'Small (under 200 sq ft)', lowIntent: true },
              { label: 'Medium (200-500 sq ft)' },
              { label: 'Large (500+ sq ft)' },
              { label: "I'm not sure", lowIntent: true },
            ],
          },
        },
        {
          id: 'pooldeck',
          label: 'Pool deck',
          followUp: {
            id: 'pooldeck_size',
            label: "What's the pool deck size?",
            type: 'radio',
            options: [
              { label: 'Under 500 sq ft' },
              { label: '500-1,000 sq ft' },
              { label: '1,000+ sq ft' },
              { label: "I'm not sure", lowIntent: true },
            ],
          },
        },
        {
          id: 'driveway',
          label: 'Driveway / walkway',
          followUp: {
            id: 'driveway_size',
            label: "What's the approximate size?",
            type: 'radio',
            options: [
              { label: 'Under 500 sq ft' },
              { label: '500-1,000 sq ft' },
              { label: '1,000+ sq ft' },
              { label: "I'm not sure", lowIntent: true },
            ],
          },
        },
        {
          id: 'basement',
          label: 'Basement floor',
          followUp: {
            id: 'basement_size',
            label: "What's the approximate size?",
            type: 'radio',
            options: [
              { label: 'Under 500 sq ft' },
              { label: '500-1,000 sq ft' },
              { label: '1,000+ sq ft' },
              { label: "I'm not sure", lowIntent: true },
            ],
          },
        },
        {
          id: 'commercial',
          label: 'Commercial / warehouse floor',
          followUp: {
            id: 'commercial_size',
            label: "What's the approximate square footage?",
            type: 'radio',
            options: [
              { label: 'Under 1,000 sq ft' },
              { label: '1,000-5,000 sq ft' },
              { label: '5,000-10,000 sq ft' },
              { label: '10,000+ sq ft' },
              { label: "I'm not sure", lowIntent: true },
            ],
          },
        },
        {
          id: 'garagestorage',
          label: 'Garage storage & organization',
          outsideLabelScope: true,
          followUp: {
            id: 'garagestorage_need',
            label: 'What kind of garage storage are you after?',
            type: 'radio',
            options: [
              { label: 'Overhead racks' },
              { label: 'Cabinets & workbench' },
              { label: 'Wall storage (slatwall, hooks, bike racks)' },
              { label: 'Full garage makeover (storage + coated floor)' },
              { label: 'Not sure yet — show me options', lowIntent: true },
            ],
          },
        },
        {
          id: 'repair',
          label: 'Concrete repair / resurfacing',
          lowIntent: true,
          followUp: {
            id: 'repair_issue',
            label: "What's the condition?",
            type: 'radio',
            options: [
              { label: 'Cracks or chips', photoPrompt: 'Add a photo of the cracks' },
              { label: 'Pitting or spalling', photoPrompt: 'Add a photo of the pitted area' },
              { label: 'Peeling old coating', photoPrompt: 'Add a photo of the peeling coating' },
              {
                label: 'Just want it refreshed',
                lowIntent: true,
                photoPrompt: 'Add a photo of the surface',
              },
            ],
          },
        },
      ],
    },
    addOns: [
      {
        id: 'garage_storage_upsell',
        label: 'Interested in adding garage storage or organization?',
        description:
          'Upsells racks, cabinets and wall storage to anyone booking a coating job.',
        type: 'radio',
        defaultEnabled: true,
        options: [
          { label: 'Yes — overhead racks' },
          { label: 'Yes — cabinets & workbench' },
          { label: 'Yes — wall storage (slatwall, hooks, bike racks)' },
          { label: 'Yes — show me everything you offer' },
          { label: 'No thanks, just the coating' },
        ],
      },
    ],
  },
  {
    id: 'concreteinstallation',
    name: 'Concrete Installation',
    icon: 'HardHat',
    tagline: 'Driveways, patios, slabs, foundations & repair',
    budgetRanges: [
      { label: 'Under $2,000' },
      { label: '$2,000 - $6,000' },
      { label: '$6,000 - $15,000' },
      { label: '$15,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What concrete work do you need?',
      services: [
        {
          id: 'driveway',
          label: 'Driveway',
          followUp: {
            id: 'driveway_job',
            label: "What's the driveway job?",
            type: 'radio',
            options: [
              { label: 'Brand-new driveway (no concrete there now)' },
              { label: 'Tear out and replace the existing driveway' },
              { label: 'Extend or widen the existing driveway' },
              { label: 'Apron or a small section only', lowIntent: true },
            ],
          },
        },
        {
          id: 'patio',
          label: 'Patio',
          followUp: {
            id: 'patio_size',
            label: "What's the patio size?",
            type: 'radio',
            options: [
              { label: 'Small (under 200 sq ft)', lowIntent: true },
              { label: 'Medium (200-500 sq ft)' },
              { label: 'Large (500+ sq ft)' },
              { label: "I'm not sure", lowIntent: true },
            ],
          },
        },
        {
          id: 'walkway',
          label: 'Sidewalks & walkways',
          followUp: {
            id: 'walkway_scope',
            label: "What's the scope?",
            type: 'radio',
            options: [
              { label: 'New sidewalk or walkway' },
              { label: 'Replace an existing one' },
              { label: 'A short section or a few squares', lowIntent: true },
            ],
          },
        },
        {
          id: 'slab',
          label: 'Slabs & foundations',
          followUp: {
            id: 'slab_purpose',
            label: "What's the slab for?",
            type: 'radio',
            options: [
              { label: 'Garage or shop' },
              { label: 'Shed or outbuilding' },
              { label: 'Home addition' },
              { label: 'House foundation or footings' },
              { label: 'Equipment or hot tub pad' },
            ],
          },
        },
        {
          id: 'steps',
          label: 'Steps & retaining walls',
          followUp: {
            id: 'steps_need',
            label: 'What are you building?',
            type: 'radio',
            options: [
              { label: 'Poured steps or stoop' },
              { label: 'Retaining wall' },
              { label: 'Curbing or edging' },
              { label: 'Not sure yet', lowIntent: true },
            ],
          },
        },
        {
          id: 'decorative',
          label: 'Stamped / decorative concrete',
          followUp: {
            id: 'decorative_finish',
            label: 'What finish are you after?',
            type: 'radio',
            options: [
              { label: 'Stamped concrete' },
              { label: 'Exposed aggregate' },
              { label: 'Colored or stained' },
              { label: 'Broom or standard finish' },
              { label: 'Not sure yet — show me options', lowIntent: true },
            ],
          },
        },
        {
          id: 'commercial',
          label: 'Commercial concrete',
          followUp: {
            id: 'commercial_work',
            label: "What's the work?",
            type: 'radio',
            options: [
              { label: 'Parking lot or drive lane' },
              { label: 'Sidewalks & ADA ramps' },
              { label: 'Building slab' },
              { label: 'Curb & gutter' },
            ],
          },
        },
        {
          id: 'removal',
          label: 'Concrete removal / tear-out',
          followUp: {
            id: 'removal_area',
            label: "What's coming out?",
            type: 'radio',
            options: [
              { label: 'Driveway', photoPrompt: 'Add a photo of the driveway' },
              { label: 'Patio', photoPrompt: 'Add a photo of the patio' },
              { label: 'Sidewalk or walkway', photoPrompt: 'Add a photo of the walkway' },
              { label: 'Slab or foundation', photoPrompt: 'Add a photo of the slab' },
            ],
          },
        },
        {
          id: 'repair',
          label: 'Concrete repair / leveling',
          lowIntent: true,
          followUp: {
            id: 'repair_issue',
            label: "What's the condition?",
            type: 'radio',
            options: [
              { label: 'Cracks', photoPrompt: 'Add a photo of the cracks' },
              {
                label: 'Sunken or uneven slab',
                photoPrompt: 'Add a photo of the sunken area',
              },
              {
                label: 'Crumbling or spalling surface',
                photoPrompt: 'Add a photo of the crumbling area',
              },
              {
                label: 'Just want it looked at',
                lowIntent: true,
                photoPrompt: 'Add a photo of the concrete',
              },
            ],
          },
        },
      ],
    },
    addOns: [
      {
        // Tear-out is the single biggest swing in a pour quote, so it's on by
        // default rather than sitting in the optional pile.
        id: 'concrete_existing',
        label: 'Is there existing concrete that needs to come out?',
        description: 'Tear-out and haul-away is the biggest swing in your price.',
        type: 'radio',
        defaultEnabled: true,
        options: [
          { label: 'Yes — it needs to be removed' },
          { label: "No — it's a fresh pour" },
          { label: "I'm not sure", lowIntent: true },
        ],
      },
      {
        id: 'concrete_access',
        label: 'Can a concrete truck reach the work area?',
        description: 'Tight access means pumping or wheelbarrowing — price it before you go.',
        type: 'radio',
        defaultEnabled: false,
        options: [
          { label: 'Yes — easy access from the street or driveway' },
          { label: 'Tight access (gate, backyard, or slope)' },
          { label: "I'm not sure", lowIntent: true },
        ],
      },
    ],
  },
  {
    id: 'exteriorlighting',
    name: 'Exterior Lighting',
    icon: 'Lightbulb',
    tagline: 'Permanent roofline, landscape & holiday lighting',
    budgetRanges: [
      { label: 'Under $1,500' },
      { label: '$1,500 - $3,500' },
      { label: '$3,500 - $8,000' },
      { label: '$8,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What type of lighting project is it?',
      services: [
        {
          id: 'permanent',
          label: 'Permanent exterior lighting',
          followUp: {
            id: 'permanent_scope',
            label: "What's the scope?",
            type: 'radio',
            options: [
              { label: 'Whole house roofline' },
              { label: 'Front of house only' },
              { label: 'Back / patio area' },
              { label: 'Small section or accent only', lowIntent: true },
            ],
          },
        },
        {
          id: 'landscape',
          label: 'Landscape / path lighting',
          followUp: {
            id: 'landscape_area',
            label: 'What needs lighting?',
            type: 'radio',
            options: [
              { label: 'Path & walkway' },
              { label: 'Trees & landscaping' },
              { label: 'Full yard landscape lighting' },
              { label: 'Just exploring options', lowIntent: true },
            ],
          },
        },
        {
          id: 'holiday',
          label: 'Holiday / seasonal lighting',
          followUp: {
            id: 'holiday_type',
            label: 'What type of install?',
            type: 'radio',
            options: [
              { label: 'Full roofline & trees' },
              { label: 'Roofline only' },
              { label: 'One-time event / small area', lowIntent: true },
              { label: 'Takedown only', lowIntent: true },
            ],
          },
        },
        {
          id: 'repair',
          label: 'Repair / maintenance',
          lowIntent: true,
          followUp: {
            id: 'repair_issue',
            label: "What's the issue?",
            type: 'radio',
            options: [
              {
                label: 'Lights not working',
                photoPrompt: "Add a photo of the lights that aren't working",
              },
              { label: 'Storm damage', photoPrompt: 'Add a photo of the storm damage' },
              {
                label: 'Bulb or section replacement',
                lowIntent: true,
                photoPrompt: 'Add a photo of the section to replace',
              },
              { label: 'Controller or app issue', photoPrompt: 'Add a photo of the controller' },
            ],
          },
        },
        {
          id: 'commercial',
          label: 'Commercial exterior lighting',
          followUp: {
            id: 'commercial_property',
            label: "What's the property?",
            type: 'radio',
            options: [
              { label: 'Retail storefront' },
              { label: 'Restaurant / hospitality' },
              { label: 'Office building' },
              { label: 'Other' },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'insulation',
    name: 'Insulation',
    icon: 'Thermometer',
    tagline: 'Attic, spray foam, fiberglass, removal & air sealing',
    budgetRanges: [
      { label: 'Under $1,000' },
      { label: '$1,000 - $3,000' },
      { label: '$3,000 - $8,000' },
      { label: '$8,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What kind of insulation work do you need?',
      services: [
        {
          id: 'attic',
          label: 'Attic insulation',
          followUp: {
            id: 'attic_scope',
            label: 'What does the attic need?',
            type: 'radio',
            options: [
              { label: 'Add insulation on top of what I have' },
              { label: 'Remove the old insulation and re-insulate' },
              { label: 'Insulate a bare or new attic' },
              {
                label: "I'm not sure what's up there",
                lowIntent: true,
                photoPrompt: 'Add a photo of your attic',
              },
            ],
          },
        },
        {
          id: 'sprayfoam',
          label: 'Spray foam insulation',
          followUp: {
            id: 'sprayfoam_area',
            label: 'Where does the spray foam go?',
            type: 'radio',
            options: [
              { label: 'Attic or roof deck' },
              { label: 'Walls' },
              { label: 'Crawl space or rim joist' },
              { label: 'Garage or outbuilding' },
              { label: "I'm not sure", lowIntent: true },
            ],
          },
        },
        {
          id: 'fiberglass',
          label: 'Fiberglass insulation',
          followUp: {
            id: 'fiberglass_type',
            label: 'What type of fiberglass install?',
            type: 'radio',
            options: [
              { label: 'Blown-in loose fill' },
              { label: 'Batts or rolls' },
              { label: "I'm not sure which I need", lowIntent: true },
            ],
          },
        },
        {
          id: 'walls',
          label: 'Wall insulation',
          followUp: {
            id: 'walls_situation',
            label: "What's the situation?",
            type: 'radio',
            options: [
              { label: 'Existing closed walls (drill and fill)' },
              { label: 'Open walls during a remodel' },
              { label: 'New construction' },
            ],
          },
        },
        {
          id: 'crawlspace',
          label: 'Crawl space / basement insulation',
          followUp: {
            id: 'crawlspace_need',
            label: 'What do you need?',
            type: 'radio',
            options: [
              { label: 'Full crawl space encapsulation' },
              { label: 'Insulation only' },
              { label: 'Basement walls or rim joist' },
              { label: 'Moisture or mold concern', photoPrompt: 'Add a photo of the problem area' },
            ],
          },
        },
        {
          id: 'removal',
          label: 'Insulation removal',
          followUp: {
            id: 'removal_reason',
            label: "What's being removed?",
            type: 'radio',
            options: [
              {
                label: 'Old or settled insulation',
                photoPrompt: 'Add a photo of the existing insulation',
              },
              { label: 'Water or mold damage', photoPrompt: 'Add a photo of the damaged area' },
              {
                label: 'Rodent or pest contamination',
                photoPrompt: 'Add a photo of the contaminated area',
              },
              { label: 'Fire or smoke damage', photoPrompt: 'Add a photo of the damage' },
            ],
          },
        },
        {
          id: 'airsealing',
          label: 'Air sealing / energy audit',
          lowIntent: true,
          followUp: {
            id: 'airsealing_reason',
            label: "What's prompting it?",
            type: 'radio',
            options: [
              { label: 'High energy bills' },
              { label: 'Drafts or uneven temperatures' },
              { label: 'Ice dams or moisture issues' },
              { label: 'Just want it evaluated', lowIntent: true },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'realestatemedia',
    name: 'Real Estate Photography',
    icon: 'Aperture',
    tagline: 'Listing photos, video, drone, 3D tours & floor plans',
    budgetRanges: [
      { label: 'Under $250' },
      { label: '$250 - $500' },
      { label: '$500 - $1,000' },
      { label: '$1,000+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What media do you need for the listing?',
      genericLabel: 'What type of shoot do you need?',
      services: [
        {
          id: 'photos',
          label: 'Listing photos',
          followUp: {
            id: 'photos_package',
            label: 'What photo package do you want?',
            type: 'radio',
            options: [
              { label: 'Standard interior & exterior' },
              { label: 'Photos + twilight shots' },
              { label: 'Premium (extra detail & lifestyle shots)' },
              { label: 'Just a few exterior shots', lowIntent: true },
            ],
          },
        },
        {
          id: 'video',
          label: 'Video tour / walkthrough',
          followUp: {
            id: 'video_type',
            label: 'What kind of video?',
            type: 'radio',
            options: [
              { label: 'Cinematic walkthrough' },
              { label: 'Vertical / social media reel' },
              { label: 'Agent-on-camera intro' },
              { label: 'Not sure yet', lowIntent: true },
            ],
          },
        },
        {
          id: 'drone',
          label: 'Drone / aerial',
          followUp: {
            id: 'drone_need',
            label: 'What do you need from the air?',
            type: 'radio',
            options: [
              { label: 'Aerial photos only' },
              { label: 'Aerial video only' },
              { label: 'Aerial photos + video' },
              { label: 'Lot lines / acreage overview' },
            ],
          },
        },
        {
          id: 'tour3d',
          label: '3D virtual tour',
          followUp: {
            id: 'tour3d_scope',
            label: "What's included?",
            type: 'radio',
            options: [
              { label: '3D tour only' },
              { label: '3D tour + floor plan' },
              { label: '3D tour bundled with photos' },
              { label: 'Just getting pricing', lowIntent: true },
            ],
          },
        },
        {
          id: 'floorplan',
          label: 'Floor plans & measurements',
          followUp: {
            id: 'floorplan_type',
            label: 'What kind of floor plan?',
            type: 'radio',
            options: [
              { label: '2D floor plan' },
              { label: '2D floor plan with room dimensions' },
              { label: 'Measured square footage report (ANSI)' },
              { label: "I'm not sure which I need", lowIntent: true },
            ],
          },
        },
        {
          id: 'virtualstaging',
          label: 'Virtual staging / photo editing',
          followUp: {
            id: 'virtualstaging_scope',
            label: 'How much needs editing?',
            type: 'radio',
            options: [
              { label: 'Whole home (most rooms)' },
              { label: 'A few key rooms' },
              { label: 'Item removal / decluttering', lowIntent: true },
              { label: 'Day-to-dusk sky edits', lowIntent: true },
            ],
          },
        },
        {
          id: 'commercial',
          label: 'Commercial property media',
          followUp: {
            id: 'commercial_property',
            label: "What's the property?",
            type: 'radio',
            options: [
              { label: 'Office' },
              { label: 'Retail' },
              { label: 'Multi-family / apartments' },
              { label: 'Land or development site' },
            ],
          },
        },
        {
          id: 'agentbranding',
          label: 'Agent headshots & branding',
          outsideLabelScope: true,
          followUp: {
            id: 'agentbranding_need',
            label: 'What are we shooting?',
            type: 'radio',
            options: [
              { label: 'Individual headshots' },
              { label: 'Team headshots' },
              { label: 'Lifestyle / brand content' },
              { label: 'Just getting pricing', lowIntent: true },
            ],
          },
        },
      ],
    },
    addOns: [
      {
        // Square footage is the number the whole quote hangs on, so this one is
        // on by default rather than sitting in the optional pile.
        id: 're_square_footage',
        label: "What's the approximate square footage of the property?",
        description: 'Drives your pricing tier. The most important question on the form.',
        type: 'radio',
        defaultEnabled: true,
        options: [
          { label: 'Under 1,500 sq ft' },
          { label: '1,500 - 2,500 sq ft' },
          { label: '2,500 - 4,000 sq ft' },
          { label: '4,000 - 6,000 sq ft' },
          { label: '6,000+ sq ft' },
          { label: "I'm not sure", lowIntent: true },
        ],
      },
      {
        id: 're_occupancy',
        label: 'What condition is the property in?',
        description: 'Tells you how much time to block and whether it is photo-ready.',
        type: 'radio',
        defaultEnabled: true,
        options: [
          { label: 'Vacant and photo-ready' },
          { label: 'Staged' },
          { label: 'Occupied / furnished' },
          { label: 'Still being cleaned or prepped', lowIntent: true },
        ],
      },
      {
        id: 're_turnaround',
        label: 'When do you need the files delivered?',
        description: 'Separates rush jobs from standard turnaround so you can price them apart.',
        type: 'radio',
        defaultEnabled: false,
        options: [
          { label: 'Next-day rush' },
          { label: 'Within 2-3 days' },
          { label: 'Standard turnaround' },
          { label: 'No deadline yet', lowIntent: true },
        ],
      },
      {
        id: 're_access',
        label: 'How will we get into the property?',
        description: 'Avoids the wasted trip when nobody is there to let you in.',
        type: 'radio',
        defaultEnabled: false,
        options: [
          { label: "I'll meet you there" },
          { label: 'Lockbox or keypad code' },
          { label: 'Owner or tenant will be home' },
          { label: 'Still working that out', lowIntent: true },
        ],
      },
    ],
  },
  {
    id: 'handyman',
    name: 'Handyman',
    icon: 'Wrench',
    tagline: 'Repairs, installs, assembly & odd jobs',
    budgetRanges: [
      { label: 'Under $150' },
      { label: '$150 - $500' },
      { label: '$500 - $1,500' },
      { label: '$1,500+' },
      { label: "I'm not sure yet", lowIntent: true },
    ],
    projectQuestion: {
      label: 'What do you need help with?',
      services: [
        {
          id: 'repairs',
          label: 'General repairs',
          followUp: {
            id: 'repairs_area',
            label: "Where's the work?",
            type: 'radio',
            options: [
              { label: 'Interior', photoPrompt: 'Add a photo of the repair inside' },
              { label: 'Exterior', photoPrompt: 'Add a photo of the repair outside' },
              { label: 'Both', photoPrompt: 'Add photos of the repairs' },
            ],
          },
        },
        {
          id: 'mounting',
          label: 'Mounting / installation',
          followUp: {
            id: 'mounting_item',
            label: "What's being installed?",
            type: 'radio',
            options: [
              { label: 'TV mount' },
              { label: 'Shelving' },
              { label: 'Fixtures / hardware' },
              { label: 'Something else' },
            ],
          },
        },
        {
          id: 'assembly',
          label: 'Furniture assembly',
          followUp: {
            id: 'assembly_amount',
            label: 'How much?',
            type: 'radio',
            options: [
              { label: 'A single item', lowIntent: true },
              { label: 'Several items' },
            ],
          },
        },
        {
          id: 'drywall',
          label: 'Drywall / patching',
          followUp: {
            id: 'drywall_scope',
            label: "What's the scope?",
            type: 'radio',
            options: [
              { label: 'Small patch or hole', lowIntent: true },
              { label: 'Multiple repairs' },
              { label: 'A whole wall or room' },
            ],
          },
        },
        {
          id: 'punchlist',
          label: 'Multiple small jobs (punch list)',
          followUp: {
            id: 'punchlist_size',
            label: 'How many tasks?',
            type: 'radio',
            options: [
              { label: 'A handful' },
              { label: 'A long list' },
              { label: 'Still putting it together', lowIntent: true },
            ],
          },
        },
      ],
    },
  },
];

export function getIndustryTemplate(industryId: string): IndustryTemplate | undefined {
  return INDUSTRY_TEMPLATES.find(i => i.id === industryId);
}

/**
 * Wording for the project question, widened when the operator offers a service
 * the specific wording doesn't cover — asking "what area do you want coated?"
 * makes no sense to someone who came in for garage storage.
 */
export function getProjectQuestionLabel(
  template: IndustryTemplate,
  selectedServiceIds: string[],
): string {
  const { label, genericLabel, services } = template.projectQuestion;
  if (!genericLabel) return label;
  const hasOutsideService = services.some(
    s => s.outsideLabelScope && selectedServiceIds.includes(s.id),
  );
  return hasOutsideService ? genericLabel : label;
}

/** Common add-ons plus any offered only by this industry (trade-specific upsells). */
export function getAddOnsForIndustry(industryId: string | null | undefined): AddOnQuestion[] {
  const template = industryId ? getIndustryTemplate(industryId) : undefined;
  return [...COMMON_ADD_ONS, ...(template?.addOns ?? [])];
}

const GENERIC_BUDGET_RANGES: TemplateOption[] = [
  { label: 'Under $1,000' },
  { label: '$1,000 - $5,000' },
  { label: '$5,000 - $15,000' },
  { label: '$15,000+' },
  { label: "I'm not sure yet", lowIntent: true },
];

/**
 * Budget answer options for the "budget" add-on, scaled to the industry.
 *
 * The lowest budget tier is flagged `lowIntent` so that, when the goal is
 * "qualified leads", the cheapest jobs route to a request instead of an instant
 * booking — filtering out low-intent / low-value leads (alongside the existing
 * "I'm not sure yet" pre-qualifier).
 */
export function getBudgetOptions(industryId: string | null | undefined): TemplateOption[] {
  const template = industryId ? getIndustryTemplate(industryId) : undefined;
  const ranges = template?.budgetRanges ?? GENERIC_BUDGET_RANGES;
  return ranges.map((opt, i) => (i === 0 ? { ...opt, lowIntent: true } : opt));
}
